import React from "react";
import { useTranslation, useLocale } from "../contexts/LocaleContext";
import { Zap } from "lucide-react";
import "./TrialBar.css";

interface TrialBarProps {
  daysRemaining: number;
  onSubscribe: (plan: "monthly" | "yearly" | "lifetime") => void;
}

export default function TrialBar({
  daysRemaining,
  onSubscribe,
}: TrialBarProps) {
  const t = useTranslation();
  const { locale } = useLocale();

  // Helper function for declensions (copied from TrialStatusContent)
  const getDaysLabel = (count: number, lang: string) => {
    if (lang === "ru") {
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return "дней";
      if (lastDigit === 1) return "день";
      if (lastDigit >= 2 && lastDigit <= 4) return "дня";
      return "дней";
    }
    if (lang === "cs") {
      if (count === 1) return "den";
      if (count >= 2 && count <= 4) return "dny";
      return "dní";
    }
    return count === 1 ? "day" : "days";
  };

  const daysLabel = getDaysLabel(daysRemaining, locale);

  // Format subtitle with days
  const barSubtitle = t.userSettings.trial.barAction
    .replace("{days}", String(daysRemaining))
    .replace("{daysLabel}", daysLabel);

  return (
    <div className="relative w-full overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white shadow-lg border-b border-white/20 py-3 px-4 z-[50]">
      {/* Shimmer Effect */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute inset-0 w-[40%] h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg] animate-shimmer" />
      </div>

      <div className="relative flex flex-col lg:flex-row items-center justify-center gap-x-10 gap-y-3 text-center">
        <div className="flex items-center gap-3 text-sm md:text-base font-bold tracking-tight">
          <Zap
            size={20}
            className="text-yellow-300 fill-yellow-300 animate-pulse"
          />
          <span className="drop-shadow-sm">{barSubtitle}</span>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 text-sm md:text-base font-bold">
          <span className="text-white drop-shadow-sm tracking-wide">
            {t.userSettings.trial.barActionLead}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onSubscribe("monthly")}
              className="bg-yellow-400 hover:bg-yellow-300 text-indigo-950 font-bold py-1.5 px-4 rounded-xl shadow-xl transition-all transform hover:scale-105 active:scale-95 whitespace-nowrap border-2 border-yellow-200"
            >
              220 Kč {t.userSettings.trialExpired.periodMonth}
            </button>
            <span className="text-white/70 lowercase font-medium italic text-xs">
              {locale === "ru" ? "либо" : locale === "cs" ? "nebo" : "or"}
            </span>
            <button
              onClick={() => onSubscribe("yearly")}
              className="bg-white/20 hover:bg-white/30 text-white font-bold py-1.5 px-4 rounded-xl border-2 border-white/40 transition-all active:scale-95 whitespace-nowrap shadow-md backdrop-blur-sm"
            >
              1600 Kč {t.userSettings.trialExpired.periodYear}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
